import EditorOps from "../EditorOps";
import { formatLiquidCrystalDateTime } from "./liquidCrystalClockFormat";
import { formatLongformClockDisplay } from "./longformClockFormat";
import { applyClockWidgetStyle } from "./clockWidgetStyle";

/**
 * Updates every clock-widget group on the canvas (all variants, multiple instances).
 * @param {import('fabric').fabric.Canvas | null | undefined} canvas
 * @returns {boolean} whether anything changed
 */
export function refreshClockWidgets(canvas) {
  if (!canvas) return false;
  const now = new Date();
  let changed = false;

  canvas.getObjects().forEach((obj) => {
    if (obj.type !== "group" || obj.eleType !== "clock-widget") return;

    const variant = obj.clockObj?.variant || "gradient-night";
    const subs = obj._objects || obj.getObjects?.() || [];

    if (variant === "liquid-crystal") {
      const next = formatLiquidCrystalDateTime(now);
      const txt = subs.find((c) => c.name === "clock-lcd-text");
      if (txt && txt.text !== next) {
        txt.set("text", next);
        changed = true;
      }
      applyClockWidgetStyle(obj, obj.clockObj);
      return;
    }

    if (variant === "longform-analog") {
      const { hourWord, minuteWord, calendarLine } = formatLongformClockDisplay(now);
      const geo = EditorOps.getLongformAnalogHandGeometry(now);
      const applyLine = (line, { len, theta }) => {
        if (!line) return;
        const deg = (theta * 180) / Math.PI;
        line.set({
          originX: "left",
          originY: "center",
          x1: 0,
          y1: 0,
          x2: len,
          y2: 0,
          angle: deg,
        });
        if (typeof line.setCoords === "function") line.setCoords();
      };
      let h = subs.find((c) => c.name === "clock-longform-hour-hand");
      let m = subs.find((c) => c.name === "clock-longform-minute-hand");
      const secG = subs.find((c) => c.name === "clock-longform-second-hand");
      if (secG && secG.type === "group") {
        secG.set({ angle: (geo.second.theta * 180) / Math.PI });
        if (typeof secG.setCoords === "function") secG.setCoords();
      }
      applyLine(h, geo.hour);
      applyLine(m, geo.minute);
      const hourT = subs.find((c) => c.name === "clock-longform-hour-text");
      const minT = subs.find((c) => c.name === "clock-longform-minute-text");
      const calT = subs.find((c) => c.name === "clock-longform-calendar-text");
      if (hourT && hourT.text !== hourWord) {
        hourT.set({ text: hourWord || "—" });
        changed = true;
      }
      if (minT && minT.text !== (minuteWord || " ")) {
        minT.set({ text: minuteWord || " " });
        changed = true;
      }
      if (calT && calT.text !== calendarLine) {
        calT.set({ text: calendarLine });
        changed = true;
      }
      [hourT, minT, calT].forEach((t) => {
        if (t && typeof t.initDimensions === "function") t.initDimensions();
      });
      applyClockWidgetStyle(obj, obj.clockObj);
      changed = true;
      return;
    }

    if (variant === "neumorphic-analog") {
      const geo = EditorOps.getNeumorphicAnalogHandGeometry(now);
      /** Radial hands: local (0,0)→(len,0) + angle; never use absolute cx/cy here (breaks group-relative left/top). */
      const applyLine = (line, { len, theta }) => {
        if (!line) return;
        const deg = (theta * 180) / Math.PI;
        line.set({
          originX: "left",
          originY: "center",
          x1: 0,
          y1: 0,
          x2: len,
          y2: 0,
          angle: deg,
        });
        if (typeof line.setCoords === "function") line.setCoords();
      };
      let h = subs.find((c) => c.name === "clock-hand-hour");
      let m = subs.find((c) => c.name === "clock-hand-minute");
      let s = subs.find((c) => c.name === "clock-hand-second");
      if (!h || !m || !s) {
        const lines = subs.filter(
          (c) => (c.type || "").toLowerCase() === "line"
        );
        if (lines.length >= 3) {
          [h, m, s] = lines.slice(-3);
        }
      }
      applyLine(h, geo.hour);
      applyLine(m, geo.minute);
      applyLine(s, geo.second);
      applyClockWidgetStyle(obj, obj.clockObj);
      changed = true;
      return;
    }

    // gradient-night (default)
    const { main, sub, dateLine } = EditorOps.formatClockFace(now);
    const timeMain = subs.find((c) => c.name === "clock-time-main");
    const timeSub = subs.find((c) => c.name === "clock-time-sub");
    const dateText = subs.find((c) => c.name === "clock-date");

    if (timeMain && timeMain.text !== main) {
      timeMain.set({ text: main });
      changed = true;
    }
    if (timeMain && typeof timeMain.initDimensions === "function") {
      timeMain.initDimensions();
    }
    const tw = timeMain?.width || 110;
    const subLeft = 15 + tw + 8;
    if (
      timeSub &&
      (timeSub.text !== sub ||
        Math.abs((timeSub.left || 0) - subLeft) > 0.5)
    ) {
      timeSub.set({ text: sub, left: subLeft });
      changed = true;
    }
    if (dateText && dateText.text !== dateLine) {
      dateText.set({ text: dateLine });
      changed = true;
    }
    applyClockWidgetStyle(obj, obj.clockObj);
  });

  if (changed) {
    canvas.requestRenderAll();
  }
  return changed;
}

/** @deprecated use refreshClockWidgets */
export const refreshLiquidCrystalClocks = refreshClockWidgets;
